import { Select } from "./ui/select.tsx";
import type * as C from "@zooi/contracts";
import { useId, useState } from "react";
import { api } from "./api.ts";
import { type LanguagePreference, languagePreference, setLanguage } from "./i18n.ts";
import { LANGUAGE_LABELS as L } from "./labels.ts";
import { ErrorText, Field } from "./ui.tsx";

/**
 * Choosing a language applies it at once; the page reloads so no screen keeps
 * labels from the other language. Signed in, it is kept on the account first
 * (plan M6 step 6b), so every device follows; on the sign-in page it stays on
 * this device.
 */
export function LanguagePicker({ account = false, compact = false }: { account?: boolean; compact?: boolean }) {
  const id = useId();
  const [value, setValue] = useState<LanguagePreference>(languagePreference);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const choose = async (next: LanguagePreference) => {
    setValue(next); setError(undefined);
    if (account) {
      setBusy(true);
      try {
        await api.put<C.Preferences>("/api/me/preferences", { locale: next === "system" ? null : next });
      } catch (cause) {
        setError(cause); setBusy(false); setValue(languagePreference());
        return;
      }
    }
    setLanguage(next);
  };
  const select = <Select variant={compact ? "compact" : "form"} label={L.title} aria-describedby={compact ? undefined : `${id}-help`} value={value} disabled={busy} onValueChange={(nextValue) => { void choose(nextValue as LanguagePreference); }} options={[{ value: "system", label: L.system }, { value: "en-US", label: L.english }, { value: "zh-CN", label: L.chinese }]} />;
  // In a footer (sign-in, consent, invite) the choice is one small select; its note is its title.
  if (compact) return <span className="z-language" title={L.note}>{select}<ErrorText error={error} /></span>;
  return <div className="z-form z-flow">
    <Field label={account ? L.interface : L.title} help={<span id={`${id}-help`}>{account ? L.accountNote : L.note}</span>}>{select}</Field>
    <ErrorText error={error} />
  </div>;
}
